export interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

/** Access token is required; refresh may be omitted (e.g. OAuth redirect with only `token`). */
export type PersistableTokens = {
  accessToken: string;
  refreshToken?: string | null;
};

export interface TokenStorage {
  getAccessToken(): string | null;
  getRefreshToken(): string | null;
  setTokens(tokens: PersistableTokens): void;
  clearTokens(): void;
  hasAccessToken(): boolean;
}

const ACCESS_COOKIE_NAME = "accessToken";
const REFRESH_STORAGE_KEY = "gmf-refresh-token";

function isProduction(): boolean {
  return process.env.NODE_ENV === "production";
}

function readCookie(name: string): string | null {
  const cookies = document.cookie.split("; ");
  for (const cookie of cookies) {
    const [cookieName, ...valueParts] = cookie.split("=");
    if (cookieName === name) {
      const value = valueParts.join("=");
      return value ? decodeURIComponent(value) : null;
    }
  }
  return null;
}

function writeAccessCookie(token: string): void {
  const secure = isProduction() ? "; Secure" : "";
  document.cookie = `${ACCESS_COOKIE_NAME}=${encodeURIComponent(token)}; path=/; SameSite=Lax${secure}`;
}

function clearAccessCookie(): void {
  const secure = isProduction() ? "; Secure" : "";
  document.cookie = `${ACCESS_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax${secure}`;
}

function readRefreshToken(): string | null {
  try {
    return sessionStorage.getItem(REFRESH_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeRefreshToken(token: string): void {
  try {
    sessionStorage.setItem(REFRESH_STORAGE_KEY, token);
  } catch {
    // sessionStorage unavailable — refresh will fail gracefully
  }
}

function clearRefreshToken(): void {
  try {
    sessionStorage.removeItem(REFRESH_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export const tokenStorage: TokenStorage = {
  getAccessToken(): string | null {
    return readCookie(ACCESS_COOKIE_NAME);
  },

  getRefreshToken(): string | null {
    return readRefreshToken();
  },

  setTokens(tokens: PersistableTokens): void {
    writeAccessCookie(tokens.accessToken);
    if (typeof tokens.refreshToken === "string" && tokens.refreshToken.length > 0) {
      writeRefreshToken(tokens.refreshToken);
    } else {
      // Avoid keeping a previous user's refresh token when OAuth returns access only.
      clearRefreshToken();
    }
  },

  clearTokens(): void {
    clearAccessCookie();
    clearRefreshToken();
  },

  hasAccessToken(): boolean {
    return Boolean(readCookie(ACCESS_COOKIE_NAME));
  },
};

/** Test-only factory with injectable storage backends. */
export function createTokenStorage(overrides?: Partial<TokenStorage>): TokenStorage {
  return { ...tokenStorage, ...overrides };
}
